import { z } from 'zod';

export const DurationSchema = z.object({
  Years: z.number(),
  Months: z.number(),
});
export type Duration = z.infer<typeof DurationSchema>;

export const JointApplicantSchema = z.object({
  ClientId: z.number(),
  ClientName: z.string(),
});
export type JointApplicant = z.infer<typeof JointApplicantSchema>;

export const ProtectionNeedsAndPreferencesSchema = z.object({
  SpecialRequirementsNotes: z.string().optional(),
  ClientHaveAWill: z.boolean().optional(),
  WillReviewedDate: z.string().optional(),
  PowerOfAttorneyArrangementInPlace: z.boolean().optional(),
  WillDetails: z.string().optional(),
  FuneralExpenses: z.number().optional(),
  MortgageLiabilities: z.number().optional(),
  LoansAndDebts: z.number().optional(),
  HouseMove: z.number().optional(),
  OtherLumpSums: z.number().optional(),
  OtherLumpSumsDescription: z.string().optional(),
  TotalLumpSumRequired: z.number().optional(),
  InvestmentsAvailableInDeath: z.number().optional(),
  InvestmentsAvailableInDeathDescription: z.string().optional(),
  IncludeEmployerBenefitsInShortfallCalculations: z.boolean().optional(),
  AdditionalBenefitsDetails: z.string().optional(),
  TotalMonthlyExpensesAfterDeath: z.number().optional(),
  TotalMonthlyExpensesAfterDeathDetails: z.string().optional(),
  EstimatedShortfallInDeath: z.number().optional(),
  DeathCoverBasis: z.number().optional(),
  DeathCoverBasisReason: z.string().optional(),
  LifeAssured: z.number().optional(),
  HowLongCoverRequired: DurationSchema.optional(),
  HowLongBenefitPayoutRequired: DurationSchema.optional(),
  ShouldPolicyBeInTrust: z.boolean().optional(),
  IsWaiverOfPremiumDeath: z.boolean().optional(),
  IsTerminalIllnessCoverDeath: z.boolean().optional(),
  IsOtherDeath: z.boolean().optional(),
  OtherBenefitsRequiredDeath: z.string().optional(),
  IncomeNeedsFurtherDetails: z.string().optional(),
  CriticalIllnessDebtCoverAmount: z.number().optional(),
  CriticalIllnessDebtCoverAmountDescription: z.string().optional(),
  CriticalIllnessOutstandingCoverAmount: z.number().optional(),
  CriticalIllnessOutstandingCoverAmountDescription: z.string().optional(),
  OtherLumpSumInCriticalIllnessTotalAmount: z.number().optional(),
  OtherLumpSumInCriticalIllnessTotalAmountDetails: z.string().optional(),
  CriticalIllnessTotalLumpSumAmount: z.number().optional(),
  MonthlyExpensesCoveredInCriticalIllness: z.number().optional(),
  MonthlyExpensesCoveredInCriticalIllnessDescription: z.string().optional(),
  InvestmentsInCriticalIllness: z.number().optional(),
  InvestmentsInCriticalIllnessDescription: z.string().optional(),
  HowLongCriticalIllnessCoverRequired: DurationSchema.optional(),
  EstimatedShortfallInCriticalIllness: z.number().optional(),
  IsWaiverOfPremiumCriticalIllness: z.boolean().optional(),
  IsTerminalIllnessCriticalIllness: z.boolean().optional(),
  IsBuyBackCriticalIllness: z.boolean().optional(),
  IsPtdCriticalIllness: z.boolean().optional(),
  IsOtherCriticalIllness: z.boolean().optional(),
  OtherBenefitsRequiredCriticalIllness: z.string().optional(),
  CriticalIllnessCoverBasis: z.number().optional(),
  ProtectionPremiumType: z.number().optional(),
  CriticalIllnessFurtherDetails: z.string().optional(),
  AccidentSicknessUnemploymentProtection: z.boolean().optional(),
  AccidentAndSicknessOnlyProtection: z.boolean().optional(),
  UnemploymentOnlyProtection: z.boolean().optional(),
  PaymentProtectionInsurance: z.boolean().optional(),
  MortgageProtectionPolicy: z.boolean().optional(),
  PreferredProtectionNotes: z.string().optional(),
  WaitPeriodUntilBenefitPayable: z.number().optional(),
  PaymentFrequency: z.number().optional(),
  ProtectionCoverBasis: z.number().optional(),
  ProtectionCoverPeriod: DurationSchema.optional(),
  ProtectionFurtherDetails: z.string().optional(),
  IsDeathProtectionImportant: z.boolean().optional(),
  IsCriticalIllnessProtectionImportant: z.boolean().optional(),
  IsUnableToWorkShortTermProtectionImportant: z.boolean().optional(),
  IsUnableToWorkLongTermProtectionImportant: z.boolean().optional(),
});
export type ProtectionNeedsAndPreferences = z.infer<typeof ProtectionNeedsAndPreferencesSchema>;

export const ClientPersonalDetailsSchema = z.object({
  MiddleName: z.string().optional(),
  FirstName: z.string().optional(),
  Surname: z.string().optional(),
  KnownAs: z.string().optional(),
  HaveYouBeenKnownByAnyOtherNames: z.boolean().optional(),
  NameChangeDate: z.string().optional(),
  PreviousTitle: z.string().optional(),
  PreviousFirstName: z.string().optional(),
  PreviousMiddleName: z.string().optional(),
  PreviousSurname: z.string().optional(),
  Retired: z.boolean().optional(),
  DateOfBirth: z.string().optional(),
  CountryOfBirth: z.string().optional(),
  SecondNationality: z.string().optional(),
  PassportCountry: z.string().optional(),
  PassportNumber: z.string().optional(),
  PassportValidFrom: z.string().optional(),
  PassportValidTo: z.string().optional(),
  IsResidentInUKForTaxWhenMortgageIsCompleted: z.boolean().optional(),
  IsApplicantADiplomat: z.boolean().optional(),
  HasDiplomaticImmunity: z.boolean().optional(),
  AreYouSeekingAsylumInUK: z.boolean().optional(),
  DoYouHoldRefugeStatus: z.boolean().optional(),
  DoYouHaveILR: z.boolean().optional(),
  HaveYouBeenUKResidentAtleastForOneYear: z.boolean().optional(),
  WillMade: z.boolean().optional(),
  WillReviewedDate: z.string().optional(),
  WillDetails: z.string().optional(),
  HasPowerOfAttorneyArrangementInPlace: z.boolean().optional(),
  InGoodHealth: z.boolean().optional(),
  Telephone: z.string().optional(),
  HomeTelephone: z.string().optional(),
  WorkTelephone: z.string().optional(),
  NINumber: z.string().optional(),
  Email: z.string().optional(),
  AdditionalInfo: z.string().optional(),
  Title: z.union([z.number(), z.string()]).optional(),
  Gender: z.string().optional(),
  MaritalStatus: z.string().optional(),
  VisaTier: z.string().optional(),
  Smoker: z.boolean().optional(),
  Vulnerable: z.boolean().optional(),
  PartnerId: z.number().optional(),
  JointApplicants: z.array(JointApplicantSchema).optional(),
  Height: z.number().optional(),
  Weight: z.number().optional(),
  DisplayUnitsAsImperial: z.boolean().optional(),
  Doctor: z.string().optional(),
  OrigoOccupationCode: z.string().optional(),
  PreExistingConditions: z.string().optional(),
  AdditionalHealthInfo: z.string().optional(),
  FactFindInviteDate: z.string().optional(),
  ProtectionNeedsAndPreferences: ProtectionNeedsAndPreferencesSchema.optional(),
});
export type ClientPersonalDetails = z.infer<typeof ClientPersonalDetailsSchema>;
